import { useEffect, useState } from "react";
import axios from "axios";
import { Autocomplete, Chip, TextField } from "@mui/material";
import { Bell, Plus, Save } from "react-feather";
import { authData } from "../../components/GetAuth";
import styles from "./companyprofile.module.css";

const MAX_EMAILS = 10;

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const normalize = (email) => (email || "").trim().toLowerCase();

const NotificationSettings = ({ emails, defaultEmail, onSaved, showMessage }) => {
  const auth = authData();
  const API_TOKEN = auth?.api_token;
  const [saving, setSaving] = useState(false);
  const [list, setList] = useState([]);
  const [input, setInput] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    setList(Array.isArray(emails) ? emails : []);
  }, [emails]);

  // Adds every address typed (comma/space/semicolon separated); returns false if any were rejected
  const addEmails = (text) => {
    const parts = text.split(/[,;\s]+/).map(normalize).filter(Boolean);
    if (!parts.length) return true;

    let next = [...list];
    let problem = "";
    parts.forEach((email) => {
      if (!EMAIL_REGEX.test(email)) problem = `${email} is not a valid email address.`;
      else if (next.includes(email)) problem = `${email} is already in the list.`;
      else if (next.length >= MAX_EMAILS) problem = `You can add up to ${MAX_EMAILS} notification emails.`;
      else next.push(email);
    });

    setList(next);
    setError(problem);
    return !problem;
  };

  const onInputChange = (e, value) => {
    // Typing a separator commits the address before it
    if (/[,;\s]$/.test(value)) {
      if (addEmails(value)) setInput("");
      else setInput(value.replace(/[,;\s]+$/, ""));
      return;
    }
    setInput(value);
    if (error) setError("");
  };

  const onChange = (e, value, reason) => {
    if (reason === "createOption") {
      if (addEmails(input)) setInput("");
      return;
    }
    // Chip removed / list cleared
    setList(value.map(normalize));
    setError("");
  };

  const onBlur = () => {
    if (input.trim() && addEmails(input)) setInput("");
  };

  const onSubmit = (e) => {
    e.preventDefault();
    // Include an address still being typed
    let emailsToSave = list;
    if (input.trim()) {
      const typed = normalize(input);
      if (!EMAIL_REGEX.test(typed)) {
        setError(`${typed} is not a valid email address.`);
        return;
      }
      if (!list.includes(typed)) emailsToSave = [...list, typed];
      setList(emailsToSave);
      setInput("");
    }

    setSaving(true);
    axios.post(process.env.REACT_APP_API_URL + "v1/company-settings/notifications", { emails: emailsToSave }, {
      headers: { Authorization: `Bearer ${API_TOKEN}` }
    }).then((response) => {
      setSaving(false);
      if (response.data.status === "SUCCESS") {
        showMessage(response.data.message, "success");
        onSaved(response.data.data);
      } else {
        showMessage(response.data.message, "error");
      }
    }).catch(() => {
      setSaving(false);
      showMessage("Could not save notification emails. Please try again.", "error");
    });
  };

  const companyEmail = normalize(defaultEmail);
  const canAddCompanyEmail = companyEmail && !list.includes(companyEmail) && list.length < MAX_EMAILS;

  return (<form onSubmit={onSubmit} noValidate>
    <section className={`${styles.Card}`}>
      <div className={`${styles.CardHead}`}>
        <div>
          <h2 className={`${styles.CardTitle}`}><Bell />Notification emails</h2>
          <p className={`${styles.CardSubtitle}`}>These addresses get a hidden copy (BCC) of the booking, shipping, delivery and invoice emails sent to your customers. Your customers won't see these addresses. Password reset and verification codes are never copied.</p>
        </div>
      </div>

      <div className={`${styles.CardBody}`}>
        <Autocomplete
          multiple
          freeSolo
          options={[]}
          value={list}
          inputValue={input}
          onInputChange={onInputChange}
          onChange={onChange}
          onBlur={onBlur}
          renderTags={(value, getTagProps) => value.map((email, index) => (
            <Chip {...getTagProps({ index })} key={email} label={email} size="small" color={email === companyEmail ? "primary" : "default"} variant={email === companyEmail ? "outlined" : "filled"} />
          ))}
          renderInput={(params) => (
            <TextField {...params} label="BCC email addresses" placeholder={list.length < MAX_EMAILS ? "Type an email and press Enter" : ""} error={!!error} inputProps={{ ...params.inputProps, inputMode: "email" }} />
          )}
        />
        {error
          ? <span className={`${styles.FieldError}`}>{error}</span>
          : <span className={`${styles.FieldHint}`}>{list.length ? `${list.length} of ${MAX_EMAILS} addresses.` : "No addresses: emails are only sent to the customer."}</span>}

        {canAddCompanyEmail && (
          <div style={{ marginTop: 12 }}>
            <button type="button" className={`${styles.AddLink}`} onClick={() => { setList([...list, companyEmail]); setError(""); }}><Plus /> Add company email ({companyEmail})</button>
          </div>
        )}
      </div>
    </section>
    <div className={`${styles.CardFooter}`} style={{ borderTop: "none", padding: "18px 0 0" }}>
      <button type="submit" className={`${styles.ActionBU} ${styles.ActionPrimary}`} disabled={saving}><Save /> {saving ? "Saving..." : "Save Notification Emails"}</button>
    </div>
  </form>);
};

export default NotificationSettings;
